import { Children, isValidElement } from 'react';
import type { ReactNode } from 'react';

import { toDsButtonTrigger } from './toDsButtonTrigger';

import type { TooltipProps } from './Tooltip';
import type { TooltipAlign, TooltipProps as DsTooltipProps, TooltipSide } from '@natter-co/design-system';

type DsProps = Pick<DsTooltipProps, 'label' | 'side' | 'align' | 'open'> & { buttonChildren?: ReactNode };

/**
 * Maps the legacy MUI `Tooltip` props onto the Design System 2.0 `Tooltip`.
 *
 * TODO(HNS-264): temporary, like the other `toDsProps` mappers; retiring the flag deletes it.
 *
 * `null` sends the caller back to MUI. Cases that stay on MUI:
 *   - a `title` that is not a string - the DS label is a string on purpose, so a node never becomes a
 *     popover by accident;
 *   - a disabled trigger that cannot map to a DS Button or IconButton;
 *   - any prop with no DS counterpart (`slotProps`, `PopperProps`, `followCursor`,
 *     `disableHoverListener`, `onOpen` / `onClose`, ...) - the DS tooltip would accept the call and silently drop them;
 *   - `data-testid` together with `disableWrapper` - on MUI it lands on the child, and the DS tooltip
 *     would put it on the popup instead.
 *
 * An empty title means "no tooltip", as it does in MUI, so it maps to an empty `label` and the
 * wrapper renders the trigger alone. Call sites use `title={cond ? text : ''}` to switch one off.
 *
 * `variant` and `display` are not refused: the DS surface is inverted in both themes so `variant` has
 * nothing left to choose, and `display` is a layout prop of the wrapper span, which stays.
 *
 * MUI's `-start` / `-end` follow the positioning engine's convention, which is exactly what `align`
 * uses - unlike Figma's `TopLeft` / `TopRight` names, which are mirrored. With no `placement` the
 * tooltip opens above, as the DS does: below, it covers the next row of a table or list.
 */
export const toDsProps = ({
  title,
  placement = 'top',
  open,
  children,
  // Layout, theme and wrapper props that do not reach the DS tooltip.
  variant: _variant,
  display: _display,
  // DS always draws its detached dot; the legacy arrow preference has no remaining choice.
  arrow: _arrow,
  disableWrapper,
  'data-testid': testId,
  ...unsupported
}: TooltipProps): DsProps | null => {
  // A prop passed as `undefined` was not passed; only a real value has to be refused.
  if (Object.values(unsupported).some((value) => value !== undefined)) return null;

  if (testId && disableWrapper) return null;

  const isEmpty = title === '' || title === null || title === undefined || title === false;
  if (!isEmpty && typeof title !== 'string') return null;
  const label = typeof title === 'string' ? title : '';

  const [side, align = 'center'] = placement.split('-');
  const tooltipProps = {
    label,
    side: side as TooltipSide,
    align: align as TooltipAlign,
    open,
  };
  const buttonChildren = toDsButtonTrigger(children, tooltipProps);
  if (buttonChildren) return { ...tooltipProps, buttonChildren };
  return hasDisabledTrigger(children) ? null : tooltipProps;
};

/**
 * Unmapped disabled controls retain MUI, whose wrapper can still receive hover. Looks
 * through the children for the first `disabled` or `aria-disabled` prop, a few levels deep because
 * call sites often wrap the control in a `<span>` or `<Box>` precisely so MUI can still hear it.
 *
 * Only sees props written at the call site: a component that disables itself from context cannot be
 * detected from here.
 */
const MAX_DEPTH = 3;

const hasDisabledTrigger = (node: ReactNode, depth = 0): boolean =>
  Children.toArray(node).some((child) => {
    if (!isValidElement<{ disabled?: unknown; 'aria-disabled'?: unknown; children?: ReactNode }>(child)) {
      return false;
    }

    const { disabled, 'aria-disabled': ariaDisabled, children } = child.props;
    if (disabled || ariaDisabled === true || ariaDisabled === 'true') return true;

    return depth < MAX_DEPTH && hasDisabledTrigger(children, depth + 1);
  });
